import { PaletteOutlineIcon } from '@carboncopyco/icons';
import type { IUser, TourTheme } from '@carboncopyco/types';
import {
  Chip,
  ColorHex,
  Dropdown,
  ExpandDownIcon,
  TextBase,
  TextCaption,
  Tooltip,
} from '@carboncopyco/ui';
import { useMemo, useRef, useState } from 'react';
import { TourThemeCategory } from './TourThemeCategory';
import { TourThemeItem } from './TourThemeItem';

type TourThemePickerProps = {
  tourThemes: TourTheme[];
  user: IUser;
  selectedThemeId: number | undefined;
  onThemeSelect: (tourTheme: TourTheme) => void;
  onThemeDelete: (tourTheme: TourTheme) => void;
};

export const TourThemePicker = ({
  tourThemes,
  user,
  selectedThemeId,
  onThemeSelect,
  onThemeDelete,
}: TourThemePickerProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const selectedTheme = tourThemes.find(
    (theme) => theme.id === selectedThemeId
  );
  const defaultThemes = useMemo(
    () => tourThemes.filter((theme) => theme.category === 'Demostack default'),
    [tourThemes]
  );
  const customThemes = useMemo(
    () => tourThemes.filter((theme) => theme.category === 'Published themes'),
    [tourThemes]
  );

  const noThemeAppliedRef = useRef<HTMLDivElement>(null);
  const noThemeApplied = !selectedThemeId;

  const archivedThemeRef = useRef<HTMLDivElement>(null);
  const isThemeArchived = !!selectedThemeId && !selectedTheme;

  const isAdminOrThemeCreator = (theme: TourTheme) => {
    if (user.isSuperUser) {
      return true;
    }

    return user.email === theme.creator;
  };

  return (
    <div className="flex flex-col pt-2.5 pb-1">
      <div className="flex flex-row">
        <PaletteOutlineIcon size={17} />
        <div className="flex flex-row justify-between  pl-2 w-full ">
          <TextBase
            label="Theme"
            color="text-ds-natural-600"
          />

          {noThemeApplied && (
            <>
              <Chip
                ref={noThemeAppliedRef}
                uppercase
                className="cursor-pointer"
              >
                no theme applied
              </Chip>
              <Tooltip
                anchorRef={noThemeAppliedRef}
                placement="bottom-end"
                shadow="lg"
                fixedWidth
              >
                <div className="p-3">
                  <TextCaption
                    label="This tour was created before using themes."
                    tagName="p"
                  />
                  <TextCaption
                    label="Once tour’s theme is applied you can not revert it."
                    tagName="p"
                  />
                </div>
              </Tooltip>
            </>
          )}

          {isThemeArchived && (
            <>
              <Chip
                color="secondary"
                uppercase
                ref={archivedThemeRef}
                className="cursor-pointer"
              >
                archived theme
              </Chip>
              <Tooltip
                anchorRef={archivedThemeRef}
                placement="bottom-end"
                shadow="lg"
                fixedWidth
              >
                <div className="p-3">
                  <TextCaption
                    label="The theme currently applied to this tour has been archived. If you apply a different theme, you won't be able to revert to the previous one."
                    tagName="p"
                  />
                </div>
              </Tooltip>
            </>
          )}
        </div>
      </div>
      <div className="pl-6">
        <div className="mb-2">
          <TextCaption
            label="Apply across the entire tour’s tooltips"
            color="text-ds-natural-300"
          />
        </div>
        <div>
          <Dropdown
            open={isOpen}
            onOpenChange={setIsOpen}
            asChild
            contentClass="!shadow-ds-md"
            zIndex={999}
            interactive
            triggerElement={
              <div>
                {selectedTheme ? (
                  <TourThemeItem
                    tourTheme={selectedTheme}
                    rightIcon={
                      <ExpandDownIcon
                        rotate={isOpen}
                        size={18}
                        color={ColorHex.primary}
                      />
                    }
                  />
                ) : (
                  <div className="flex p-2 mb-2 border border-ds-natural-150 rounded-lg justify-between items-center cursor-pointer">
                    <TextBase label="Select tour theme" />
                    <ExpandDownIcon
                      rotate={isOpen}
                      size={18}
                      color={ColorHex.primary}
                    />
                  </div>
                )}
              </div>
            }
          >
            <div className="p-4 w-[280px] max-h-[400px] overflow-y-auto">
              <TourThemeCategory
                label="demostack default"
                tourThemes={defaultThemes}
                selectedThemeId={selectedThemeId}
                onThemeSelect={onThemeSelect}
              />
              {customThemes.length > 0 && (
                <TourThemeCategory
                  label={`published themes (${customThemes.length})`}
                  tourThemes={customThemes}
                  selectedThemeId={selectedThemeId}
                  onThemeSelect={onThemeSelect}
                  showMoreOptionsThemeDropdown={isAdminOrThemeCreator}
                  onThemeDelete={(theme) => {
                    setIsOpen(false);
                    onThemeDelete(theme);
                  }}
                />
              )}
            </div>
          </Dropdown>
        </div>
      </div>
    </div>
  );
};
